import { DataTable } from "@/components/molecules/tables/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateClient,
  useMyClients,
  usePendingInvitations,
  withoutDemoSpaces,
} from "@/features/clients/hooks/use-clients";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { BuildingApartmentIcon, PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../backoffice/layout";
import { useCompaniesColumns } from "./components/companies-columns";
import { useInvitationsColumns } from "./components/invitations-columns";

export function CompaniesPage() {
  const { t } = useTranslation("companies");
  useTitle(t("title"));
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");

  const { data: allClients = [], isLoading: isLoadingClients } = useMyClients();
  // The demo spaces this user imported are reached from /admin/demo-spaces.
  const clients = withoutDemoSpaces(allClients);
  const { data: invitations = [], isLoading: isLoadingInvitations } =
    usePendingInvitations();
  const createClient = useCreateClient();
  const companiesColumns = useCompaniesColumns();
  const invitationsColumns = useInvitationsColumns();

  // Someone with nothing yet belongs here too: this is the page that opens a
  // company, and the empty state below is how. Sending them to the landing page
  // instead left that state unreachable — and the landing page, which only
  // signposts, would send them right back.
  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) return;

    try {
      const result = await createClient.mutateAsync({ name: companyName });
      toast.success(t("messages.companyCreated"));
      setIsCreateOpen(false);
      setCompanyName("");
      // Straight into onboarding: a brand-new tenant has nothing but seeded
      // defaults, and the wizard is what turns those into a usable agent.
      navigate(`/${result.client.id}/onboarding`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.companyCreateFailed"),
      );
    }
  };

  return (
    <PageLayout
      title={t("title")}
      description={t("description")}
      actions={
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("actions.createCompany")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreateCompany}>
              <DialogHeader>
                <DialogTitle>{t("createDialog.title")}</DialogTitle>
                <DialogDescription>
                  {t("createDialog.description")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("createDialog.companyName")}</Label>
                  <Input
                    id="name"
                    placeholder={t("createDialog.companyNamePlaceholder")}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                >
                  {t("createDialog.cancel")}
                </Button>
                <Button type="submit" disabled={createClient.isPending}>
                  {createClient.isPending
                    ? t("createDialog.creating")
                    : t("createDialog.create")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {t("sections.pendingInvitations")}
          </h3>
          <DataTable
            columns={invitationsColumns}
            data={invitations}
            isLoading={isLoadingInvitations}
            hideSearch={true}
          />
        </div>
      )}

      {/* My Companies */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("sections.myCompanies")}</h3>
        {isLoadingClients ? (
          <div className="text-muted-foreground">{t("table.loading")}</div>
        ) : clients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BuildingApartmentIcon
                className="h-12 w-12 mx-auto mb-4"
                weight="fill"
              />
              <p className="text-muted-foreground mb-4">
                {t("messages.noCompanies")}
              </p>
              <Button onClick={() => setIsCreateOpen(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                {t("actions.createFirstCompany")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <DataTable
            columns={companiesColumns}
            data={clients}
            isLoading={isLoadingClients}
            hideSearch={true}
          />
        )}
      </div>
    </PageLayout>
  );
}
